import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://yoybuy.asia";
const articleUrl = `${siteUrl}/article/yoybuy-sheet-guide`;

const articleJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      "@id": `${articleUrl}#article`,
      "headline": "Полное руководство по Yoybuy Sheet: как организовать заказы через таблицы",
      "description": "Пошаговое руководство по использованию Yoybuy Sheet — инновационной spreadsheet-системы для заказов из Китая через Kaspreadsheet.",
      "author": { "@type": "Organization", "name": "YoyBuy Asia", "url": siteUrl + "/" },
      "datePublished": "2026-07-05",
      "dateModified": "2026-07-05",
      "publisher": { "@type": "Organization", "name": "YoyBuy Asia", "@id": `${siteUrl}/#organization` },
      "mainEntityOfPage": { "@type": "WebPage", "@id": articleUrl },
      "inLanguage": "ru",
      "isPartOf": { "@id": `${siteUrl}/#website` }
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${articleUrl}#breadcrumb`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Главная",
          "item": siteUrl + "/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Руководство по Yoybuy Sheet",
          "item": articleUrl
        }
      ]
    }
  ]
};

export default function ArticlePage() {
  const { t } = useTranslation();

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 px-6 md:px-10">
        <div className="max-w-[860px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              {t("article_breadcrumb_home")}
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-700">{t("article_breadcrumb_current")}</span>
          </nav>

          <div className="mb-8">
            <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-800 rounded-full text-xs font-semibold mb-4">
              {t("article_1_type")}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
              {t("article_page_title")}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 mt-3">
              {t("article_page_subtitle")}
            </p>
          </div>

          <div className="w-full h-[300px] md:h-[420px] rounded-2xl overflow-hidden mb-10 bg-background-100">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20laptop%20displaying%20detailed%20product%20ordering%20spreadsheet%20with%20sample%20products%20nearby%20on%20minimalist%20wooden%20desk%20soft%20natural%20lighting%20warm%20neutral%20tones%20professional%20editorial%20photography&width=1200&height=600&seq=article-hero-guide&orientation=landscape"
              alt={t("article_page_title")}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <article className="prose-custom">
            <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-6">
              <p>
                <strong>Yoybuy Sheet</strong> — это инновационная система оформления заказов из Китая, которая кардинально меняет подход к онлайн-шоппингу. Вместо того чтобы вручную добавлять каждый товар в корзину через веб-интерфейс, пользователи <strong>Yoybuy Sheet</strong> могут загружать целые таблицы с сотнями позиций одновременно. Эта технология, разработанная командой <strong>Kaspreadsheet</strong>, позволяет экономить до 70% времени при оформлении массовых заказов и полностью исключает ошибки ручного ввода.
              </p>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                Что такое Yoybuy Sheet и как это работает
              </h2>
              <p>
                <strong>Yoybuy Sheet</strong> представляет собой специализированный формат электронных таблиц, оптимизированный для работы с платформой <strong>Kaspreadsheet</strong>. Пользователь заполняет шаблон таблицы, указывая артикулы товаров, размеры, цвета и количество, после чего система автоматически обрабатывает заказ, рассчитывает стоимость с учетом доставки и формирует итоговую смету. <strong>Spreadsheet-заказы</strong> через <strong>Yoybuy Sheet</strong> поддерживают все категории товаров: от обуви и одежды до аксессуаров и парфюмерии.
              </p>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                Преимущества использования Yoybuy Sheet
              </h2>
              <p>
                Главное преимущество <strong>Yoybuy Sheet</strong> заключается в скорости обработки. Если при традиционном оформлении заказа на 50 позиций требуется около часа, то через <strong>spreadsheet-систему</strong> этот процесс занимает не более 10 минут. Кроме того, <strong>Yoybuy Sheet</strong> автоматически проверяет наличие товаров, актуальность цен и совместимость размерных сеток, что исключает человеческий фактор. Для владельцев интернет-магазинов <strong>Yoybuy Sheet</strong> становится незаменимым инструментом, позволяющим управлять закупками в промышленных масштабах.
              </p>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                Пошаговая инструкция по работе с Yoybuy Sheet
              </h2>
              <ol className="list-decimal pl-5 space-y-4">
                <li>
                  <strong>Скачайте шаблон таблицы</strong> с платформы <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">Kaspreadsheet</a>. Шаблон <strong>Yoybuy Sheet</strong> доступен в форматах Google Sheets и Microsoft Excel.
                </li>
                <li>
                  <strong>Заполните данные о товарах</strong>: вставьте ссылки на товары, укажите артикулы (SKU), выберите размеры и цвета, задайте количество для каждой позиции.
                </li>
                <li>
                  <strong>Загрузите заполненную таблицу</strong> в личный кабинет на <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">Kaspreadsheet</a>. Система автоматически проверит все позиции и рассчитает итоговую стоимость.
                </li>
                <li>
                  <strong>Подтвердите и оплатите заказ</strong>. После обработки <strong>Yoybuy Sheet</strong> сформирует детальный отчет с разбивкой по каждому товару.
                </li>
                <li>
                  <strong>Отслеживайте статус доставки</strong> в реальном времени через интерфейс платформы. Все этапы — от покупки у поставщика до прибытия на склад — отображаются в единой <strong>spreadsheet-панели</strong>.
                </li>
              </ol>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                Yoybuy Sheet для бизнеса: оптовые закупки и реселлинг
              </h2>
              <p>
                Для предпринимателей <strong>Yoybuy Sheet</strong> открывает новые возможности масштабирования. Система поддерживает пакетную обработку заказов от разных поставщиков, автоматическую консолидацию на складе и гибкие настройки логистики. <strong>Spreadsheet-заказы</strong> через <strong>Yoybuy Sheet</strong> интегрируются с популярными платформами для интернет-торговли, что позволяет настроить прямой экспорт данных о товарах в карточки маркетплейсов. По статистике <strong>Kaspreadsheet</strong>, пользователи <strong>Yoybuy Sheet</strong> увеличивают объем закупок в среднем на 40% в течение первых трех месяцев использования.
              </p>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                Безопасность и надежность Yoybuy Sheet
              </h2>
              <p>
                Все данные, передаваемые через <strong>Yoybuy Sheet</strong>, шифруются с использованием современных протоколов безопасности. Платформа <strong>Kaspreadsheet</strong> гарантирует конфиденциальность информации о заказах и платежных данных. Каждый <strong>spreadsheet-заказ</strong> проходит многоуровневую верификацию, что исключает мошеннические операции и ошибки в расчетах. Служба поддержки <strong>Yoybuy Sheet</strong> работает в режиме 24/7 и готова помочь с любыми вопросами на русском языке.
              </p>

              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                Часто задаваемые вопросы о Yoybuy Sheet
              </h2>

              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    Подходит ли Yoybuy Sheet для новичков?
                  </h3>
                  <p>
                    Да, <strong>Yoybuy Sheet</strong> разработан с учетом потребностей пользователей с любым уровнем подготовки. Интерфейс интуитивно понятен, а подробная документация на русском языке поможет быстро освоить все функции <strong>spreadsheet-системы</strong>.
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    Сколько стоит использование Yoybuy Sheet?
                  </h3>
                  <p>
                    <strong>Yoybuy Sheet</strong> является бесплатным инструментом платформы <strong>Kaspreadsheet</strong>. Вы платите только за стоимость товаров и доставку, без дополнительных комиссий за использование <strong>spreadsheet-системы</strong>.
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    Какие категории товаров поддерживает Yoybuy Sheet?
                  </h3>
                  <p>
                    <strong>Yoybuy Sheet</strong> поддерживает все 14 категорий товаров, доступных на <strong>Kaspreadsheet</strong>: обувь, худи и свитера, футболки, куртки, штаны и шорты, головные уборы, комплекты, нижнее белье, джерси, аксессуары, сумки, ремни, парфюмерия и прочие товары.
                  </p>
                </div>
              </div>

              <div className="mt-10 p-6 md:p-8 bg-primary-50 rounded-2xl border border-primary-100">
                <h3 className="text-lg font-heading font-bold text-primary-800 mb-3">
                  Готовы попробовать Yoybuy Sheet?
                </h3>
                <p className="text-sm text-primary-700 mb-5">
                  Перейдите на <strong>Kaspreadsheet</strong> и начните использовать <strong>Yoybuy Sheet</strong> для ваших заказов уже сегодня. Экономьте время и деньги с умной <strong>spreadsheet-системой</strong>.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t("nav_yoybuy_sheet")}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 border-2 border-secondary-300 text-foreground-700 rounded-full text-sm font-semibold hover:border-secondary-500 hover:text-foreground-950 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {t("article_breadcrumb_home")}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <Footer />

      <JsonLd data={articleJsonLd} />
    </main>
  );
}